import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CreditCard, FileSpreadsheet, Tags, Settings2, Search, RotateCcw, CheckSquare, PackageCheck, Printer, Eye,
  ListChecks, Undo2, ShieldAlert, ChevronLeft, ChevronRight, ClipboardCheck,
} from "lucide-react";
import { api, idr, pesanGalat, fmtNum } from "../../lib/api";
import { formatTanggal } from "../../lib/tanggal";
import { useDebounce } from "../../hooks/useDebounce";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta } from "../../components/ui/responsive-table";
import {
  MDR_BASE, ST_ELIGIBLE, ST_PERLU_REVIEW, STATUS_TAMPIL, STATUS_META, StatusPill, Modal, DialogAlasan, useHakMdr,
  unduhBerkas, unduhDenganToast, pesanGalatUnduh, kelasInput, kelasTombol, kelasTombolUtama, kelasTombolKecil,
} from "./claimMdr/shared";
import DialogImporData from "./claimMdr/DialogImporData";
import DialogSrp from "./claimMdr/DialogSrp";
import DialogPengaturan from "./claimMdr/DialogPengaturan";
import { DialogKlaim, DialogKandidat, DialogInvoice } from "./claimMdr/DialogKlaim";
import PanelBatch from "./claimMdr/PanelBatch";

/** Finance › Piutang (AR) › Claim MDR (brief owner 22 Sep 2026 bagian C,
 *  keputusan C1-C16).
 *
 *  Alurnya: Import Data Claim (satu berkas semua tab) + Master SRP Promo →
 *  server mencocokkan tiap baris dengan penjualan LIVE dan memberi status
 *  (Eligible / Tidak Eligible / Perlu Review / Data Duplikat) + alasan →
 *  finance mencentang Eligible → "Siap Claim" (data PP & nota dikunci) →
 *  Export Claim = batch bernomor (Excel format owner) → "Tandai Sudah Diklaim"
 *  → Catat CN dari distributor. Tab = distributor (bawaan TAM & GDN, bisa
 *  diubah dari "Daftar Leasing").
 *
 *  Semua penyaringan (status/leasing/cari/tanggal/impor) dijalankan DI SERVER —
 *  daftar dihitung sampai 5000 baris lalu dipotong per halaman; menyaring di
 *  layar membuat ringkasan membantah isi tabel (CLAUDE.md §23). Centangan di
 *  layar hanya PERMINTAAN: server menghitung ulang statusnya saat Siap Claim.
 *
 *  Tombol memakai `can("finance_claim_mdr", aksi)` — kunci & aksi SAMA dengan
 *  `require_permission` endpoint-nya (aturan #3); tidak ada tombol hover-only. */

const PER_HALAMAN = 100;

export default function ClaimMdr() {
  const hak = useHakMdr();
  const [sp, setSp] = useSearchParams();

  const [pengaturan, setPengaturan] = useState(null);
  const [versiPeng, setVersiPeng] = useState(0);
  const tabs = useMemo(() => pengaturan?.tabs || [], [pengaturan]);
  const tabAktif = (sp.get("tab") || "").toUpperCase();
  const tab = tabs.find((t) => t.kode === tabAktif) || tabs.find((t) => t.aktif !== false) || tabs[0] || null;

  const [statusF, setStatusF] = useState("");
  const [leasingF, setLeasingF] = useState("");
  const [cari, setCari] = useState("");
  const cariDeb = useDebounce(cari, 400);
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const [imporF, setImporF] = useState(null);
  const [halaman, setHalaman] = useState(1);

  const [data, setData] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [galatMuat, setGalatMuat] = useState(null);
  const [muatUlang, setMuatUlang] = useState(0);
  const segarkan = () => setMuatUlang((n) => n + 1);
  const [pilih, setPilih] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [hasilSiap, setHasilSiap] = useState(null);
  const [pengajuan, setPengajuan] = useState([]);

  const [dlgImpor, setDlgImpor] = useState(false);
  const [dlgSrp, setDlgSrp] = useState(false);
  const [dlgPeng, setDlgPeng] = useState(false);
  const [dlgAntrean, setDlgAntrean] = useState(false);
  const [dlgKandidat, setDlgKandidat] = useState(null);
  const [dlgTinjau, setDlgTinjau] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const klaimParam = sp.get("klaim");

  // Pengaturan (tab + leasing) — dimuat ulang setelah disimpan dari dialog.
  useEffect(() => {
    if (!hak.lihat) return;
    let batal = false;
    api.get(`${MDR_BASE}/pengaturan`).then((r) => { if (!batal) setPengaturan(r.data.pengaturan); })
      .catch((e) => { if (!batal) toast.error(pesanGalat(e, "Gagal memuat pengaturan Claim MDR")); });
    return () => { batal = true; };
  }, [hak.lihat, versiPeng]);

  const paramsFilter = useCallback(() => {
    const p = { tab: tab?.kode };
    if (statusF) p.status = statusF;
    if (leasingF) p.leasing = leasingF;
    if (cariDeb.trim()) p.q = cariDeb.trim();
    if (dari) p.dari = dari;
    if (sampai) p.sampai = sampai;
    if (imporF) p.impor_id = imporF.id;
    return p;
  }, [tab?.kode, statusF, leasingF, cariDeb, dari, sampai, imporF]);

  // Filter berubah = kembali ke halaman 1 dan centangan dibuang (baris yang
  // dicentang bisa tidak lagi tampil — mengirimnya diam-diam membingungkan).
  useEffect(() => { setHalaman(1); setPilih(new Set()); }, [tab?.kode, statusF, leasingF, cariDeb, dari, sampai, imporF]);

  useEffect(() => {
    if (!hak.lihat || !tab) return;
    let batal = false;
    setMemuat(true); setGalatMuat(null);
    api.get(`${MDR_BASE}/data`, { params: { ...paramsFilter(), halaman, per_halaman: PER_HALAMAN }, timeout: 120000 })
      .then((r) => { if (!batal) setData(r.data); })
      .catch((e) => { if (!batal) { setGalatMuat(pesanGalat(e, "Gagal memuat data claim")); setData(null); } })
      .finally(() => { if (!batal) setMemuat(false); });
    return () => { batal = true; };
  }, [hak.lihat, tab, paramsFilter, halaman, muatUlang]);

  useEffect(() => {
    if (!hak.lihat) return;
    api.get(`${MDR_BASE}/pengajuan-batal`).then((r) => setPengajuan(r.data.items || [])).catch(() => {});
  }, [hak.lihat, muatUlang]);

  if (!hak.lihat) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="mdr-tanpa-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Claim MDR</p>
          <p className="text-sm text-slate-500 mt-1">Minta admin memberi izin modul "Finance · Claim MDR".</p>
        </div>
      </div>
    );
  }

  const items = data?.items || [];
  const ringkasan = data?.ringkasan || {};
  const totalHalaman = Math.max(1, Math.ceil((data?.jumlah || 0) / PER_HALAMAN));
  const bisaDicentang = (b) => hak.ubah && b.status === ST_ELIGIBLE && !b.klaim_id;
  const barisCentang = items.filter(bisaDicentang);
  const semuaTercentang = barisCentang.length > 0 && barisCentang.every((b) => pilih.has(b.data_id));
  const togglePilih = (id) => setPilih((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSemua = () => setPilih(semuaTercentang ? new Set() : new Set(barisCentang.map((b) => b.data_id)));
  const leasingOpsi = (pengaturan?.leasing || []).filter((e) => !tab || (e.tab || []).includes(tab.kode));
  const tabBisaProses = tab && tab.aktif !== false;
  // Hitungan server = filter yang SAMA dengan pembuat batch (Siap Claim, belum
  // masuk batch, bukan Perlu Review) — bukan ringkasan Siap Claim yang ikut
  // menghitung klaim yang sudah diekspor.
  const siapEkspor = data?.siap_ekspor || 0;

  const gantiTab = (kode) => { const n = new URLSearchParams(sp); n.set("tab", kode); n.delete("klaim"); setSp(n, { replace: true }); };
  const tutupKlaim = () => { const n = new URLSearchParams(sp); n.delete("klaim"); setSp(n, { replace: true }); };
  const bukaKlaim = (kid) => { const n = new URLSearchParams(sp); n.set("klaim", kid); setSp(n, { replace: true }); };

  const tandaiSiap = async () => {
    setBusy(true); setHasilSiap(null);
    try {
      const r = await api.post(`${MDR_BASE}/klaim`, { tab: tab.kode, data_ids: [...pilih] });
      (r.data.berhasil?.length ? toast.success : toast.error)(r.data.message);
      if (r.data.gagal?.length) setHasilSiap(r.data.gagal);
      setPilih(new Set());
      segarkan();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menandai Siap Claim")); }
    finally { setBusy(false); }
  };

  const exportClaim = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${MDR_BASE}/batch`, { tab: tab.kode });
      toast.success(r.data.message);
      try { await unduhBerkas(`${MDR_BASE}/batch/${r.data.id}/xlsx`, `claim-mdr-${r.data.kode}.xlsx`); }
      catch (e) { toast.error(await pesanGalatUnduh(e, "Batch dibuat, tapi unduhan gagal — unduh ulang dari daftar batch")); }
      segarkan();
    } catch (e) { toast.error(pesanGalat(e, "Gagal membuat batch Export Claim")); }
    finally { setBusy(false); }
  };

  const hapusPilihan = async (b) => {
    try {
      const r = await api.delete(`${MDR_BASE}/data/${b.data_id}/pilih-kandidat`);
      toast.success(r.data.message || "Pilihan dihapus");
      segarkan();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menghapus pilihan")); }
  };

  // "Perlu Review" satu transaksi (nama beda / SRP − DP ≠ Nominal / aktivasi
  // berubah) diterima setelah ditinjau — server menghitung ulang dan menolak
  // alasan yang tidak boleh diterima; tombolnya hanya muncul kalau
  // `bisa_terima_tinjauan` (aturan yang sama dengan endpoint).
  const terimaTinjauan = async (catatan) => {
    try {
      const r = await api.post(`${MDR_BASE}/data/${dlgTinjau.data_id}/terima-tinjauan`, { tab: tab.kode, catatan });
      toast.success(r.data.message || "Diterima setelah ditinjau");
      setDlgTinjau(null);
      segarkan();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menyimpan tinjauan")); }
  };
  const cabutTinjauan = async (b) => {
    try {
      const r = await api.delete(`${MDR_BASE}/data/${b.data_id}/terima-tinjauan`);
      toast.success(r.data.message || "Tinjauan dicabut");
      segarkan();
    } catch (e) { toast.error(pesanGalat(e, "Gagal mencabut tinjauan")); }
  };

  const bukaInvoice = (b) => setInvoice(b.klaim_id
    ? { url: `${MDR_BASE}/klaim/${b.klaim_id}/invoice` }
    : { url: `${MDR_BASE}/data/${b.data_id}/invoice`, params: { tab: tab.kode } });

  const resetFilter = () => { setStatusF(""); setLeasingF(""); setCari(""); setDari(""); setSampai(""); setImporF(null); };
  const filterAktif = statusF || leasingF || cari || dari || sampai || imporF;

  /** Tombol aksi SATU baris — dipakai tabel & kartu (akhiran testid beda). */
  const aksiBaris = (b, akhiran = "") => (
    <div className="flex flex-wrap gap-1">
      {b.klaim_id && (
        <button type="button" className={kelasTombolKecil} onClick={() => bukaKlaim(b.klaim_id)}
                data-testid={`mdr-row-detail-${b.data_id}${akhiran}`}><Eye className="w-3.5 h-3.5" /> Detail</button>
      )}
      {hak.ubah && !b.klaim_id && b.status === ST_PERLU_REVIEW && (b.kandidat || []).length > 1 && (
        <button type="button" className={kelasTombolKecil} onClick={() => setDlgKandidat(b)}
                data-testid={`mdr-row-pilih-${b.data_id}${akhiran}`}><ListChecks className="w-3.5 h-3.5" /> Pilih Transaksi</button>
      )}
      {hak.ubah && !b.klaim_id && b.pilihan_manual && (
        <button type="button" className={kelasTombolKecil} onClick={() => hapusPilihan(b)}
                data-testid={`mdr-row-hapus-pilihan-${b.data_id}${akhiran}`}><Undo2 className="w-3.5 h-3.5" /> Hapus Pilihan</button>
      )}
      {hak.ubah && !b.klaim_id && b.bisa_terima_tinjauan && (
        <button type="button" className={kelasTombolKecil} onClick={() => setDlgTinjau(b)}
                data-testid={`mdr-row-terima-${b.data_id}${akhiran}`}><ClipboardCheck className="w-3.5 h-3.5" /> Terima Setelah Ditinjau</button>
      )}
      {hak.ubah && !b.klaim_id && b.tinjauan && (
        <button type="button" className={kelasTombolKecil} onClick={() => cabutTinjauan(b)}
                data-testid={`mdr-row-cabut-tinjauan-${b.data_id}${akhiran}`}><Undo2 className="w-3.5 h-3.5" /> Cabut Tinjauan</button>
      )}
      {(b.klaim_id || b.status === ST_ELIGIBLE) && (
        <button type="button" className={kelasTombolKecil} onClick={() => bukaInvoice(b)}
                data-testid={`mdr-row-invoice-${b.data_id}${akhiran}`}><Printer className="w-3.5 h-3.5" /> Invoice Kredit</button>
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="finance-claim-mdr">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Piutang Usaha</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-emerald-600" /> Claim MDR
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Claim transaksi leasing / kartu kredit ke distributor — cocokkan Data Claim dengan penjualan, tandai Siap Claim, lalu ekspor per batch.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className={hak.impor ? kelasTombolUtama : kelasTombol} onClick={() => setDlgImpor(true)}
                  data-testid="mdr-btn-impor-data">
            <FileSpreadsheet className="w-4 h-4" /> {hak.impor ? "Import Data Claim" : "Riwayat Impor Data"}
          </button>
          <button type="button" className={kelasTombol} onClick={() => setDlgSrp(true)} data-testid="mdr-btn-srp">
            <Tags className="w-4 h-4" /> {hak.impor ? "Import Master SRP Promo" : "Master SRP Promo"}
          </button>
          <button type="button" className={kelasTombol} onClick={() => setDlgPeng(true)} data-testid="mdr-btn-leasing">
            <Settings2 className="w-4 h-4" /> Daftar Leasing
          </button>
          {pengajuan.length > 0 && (
            <button type="button" onClick={() => setDlgAntrean(true)} data-testid="mdr-btn-pengajuan"
                    className="h-10 px-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 text-sm font-medium inline-flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4" /> Pengajuan Batal ({pengajuan.length})
            </button>
          )}
        </div>
      </div>

      {/* Tab distributor — dari pengaturan (bawaan TAM & GDN). */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto overflow-y-hidden" data-testid="mdr-tabs">
        {tabs.map((t) => (
          <button key={t.kode} type="button" onClick={() => gantiTab(t.kode)} data-testid={`mdr-tab-${t.kode}`}
                  className={`px-4 h-10 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                    tab?.kode === t.kode ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.nama || t.kode}{t.aktif === false && <span className="ml-1 text-[10px] font-normal text-slate-400">(nonaktif)</span>}
          </button>
        ))}
      </div>

      {tab && tab.aktif === false && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="mdr-tab-nonaktif">
          Tab {tab.kode} nonaktif — klaim & batch lamanya tetap bisa dilihat, tapi data baru tidak diproses di sini.
        </p>
      )}

      {/* Ringkasan per status (klik = filter). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="mdr-ringkasan">
        {STATUS_TAMPIL.map((s) => (
          <button key={s} type="button" onClick={() => setStatusF(statusF === s ? "" : s)} data-testid={`mdr-ringkasan-${s}`}
                  className={`text-left bg-white border rounded-xl p-3 shadow-sm ${statusF === s ? "border-emerald-500 ring-1 ring-emerald-500" : "border-slate-200"}`}>
            <StatusPill status={s} />
            <p className="font-display text-lg font-bold text-slate-900 mt-1">{fmtNum(ringkasan[s]?.jumlah)}</p>
            <p className="text-[11px] text-slate-500 font-mono">{idr(ringkasan[s]?.nominal)}</p>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className={`${kelasInput} min-w-[160px]`}
                  data-testid="mdr-filter-status">
            <option value="">Semua Status</option>
            {STATUS_TAMPIL.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <select value={leasingF} onChange={(e) => setLeasingF(e.target.value)} className={`${kelasInput} min-w-[160px]`}
                  data-testid="mdr-filter-leasing">
            <option value="">Semua Leasing/CC</option>
            {leasingOpsi.map((e) => <option key={e.kode} value={e.kode}>{e.nama}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari IMEI, SN, nama, No. Kontrak, nota..."
                   className={`${kelasInput} w-full pl-9`} data-testid="mdr-filter-cari" />
          </div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1">Tgl beli
            <input type="date" value={dari} onChange={(e) => setDari(e.target.value)} className={kelasInput} data-testid="mdr-filter-dari" />
          </label>
          <span className="text-xs text-slate-400">s.d.</span>
          <input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)} className={kelasInput} data-testid="mdr-filter-sampai" />
          {imporF && (
            <button type="button" onClick={() => setImporF(null)} className={kelasTombolKecil} data-testid="mdr-filter-impor-hapus">
              Impor {imporF.kode} ✕
            </button>
          )}
          {filterAktif && (
            <button type="button" onClick={resetFilter} data-testid="mdr-filter-reset"
                    className="h-10 px-3 rounded-lg text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
          <div className="flex-1" />
          <button type="button" className={kelasTombol} data-testid="mdr-ekspor-tabel" disabled={!tab}
                  onClick={() => unduhDenganToast(`${MDR_BASE}/data/export-xlsx`, `data-claim-mdr-${tab?.kode}.xlsx`, paramsFilter(), "Gagal mengekspor tabel")}>
            <FileSpreadsheet className="w-4 h-4" /> Ekspor Tabel
          </button>
        </div>

        {hak.ubah && tabBisaProses && (
          <div className="flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
            <button type="button" onClick={tandaiSiap} disabled={busy || pilih.size === 0} className={kelasTombolUtama}
                    data-testid="mdr-btn-siap">
              <CheckSquare className="w-4 h-4" /> Tandai Siap Claim{pilih.size ? ` (${pilih.size})` : ""}
            </button>
            <button type="button" onClick={exportClaim} disabled={busy || siapEkspor === 0} className={kelasTombol}
                    data-testid="mdr-btn-export-claim" title="Kumpulkan semua Siap Claim tab ini (yang belum masuk batch) jadi satu batch bernomor">
              <PackageCheck className="w-4 h-4" /> Export Claim{siapEkspor ? ` (${siapEkspor})` : ""}
            </button>
            <p className="text-xs text-slate-500">Centang baris Eligible → Siap Claim. Export Claim mengambil HANYA data Siap Claim yang belum masuk batch.</p>
          </div>
        )}
        {hasilSiap && (
          <div className="text-xs border border-rose-200 bg-rose-50 rounded-lg p-3" data-testid="mdr-siap-gagal">
            <p className="font-semibold text-rose-800 mb-1">Tidak bisa ditandai Siap Claim:</p>
            <ul className="space-y-0.5">{hasilSiap.map((g) => <li key={g.data_id}>{g.alasan}</li>)}</ul>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        {galatMuat && <p className="p-4 text-sm text-rose-700" data-testid="mdr-galat">{galatMuat}</p>}
        <TabelDesktop>
          <table className="w-full text-xs min-w-[980px]">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 w-8">
                  {hak.ubah && barisCentang.length > 0 && (
                    <input type="checkbox" checked={semuaTercentang} onChange={toggleSemua} data-testid="mdr-centang-semua" />
                  )}
                </th>
                <th className="text-left px-2 py-2">Tgl Aktivasi</th>
                <th className="text-left px-2 py-2">Tgl Pembelian</th>
                <th className="text-left px-2 py-2">IMEI</th>
                <th className="text-left px-2 py-2">SN</th>
                <th className="text-left px-2 py-2">Tipe Produk</th>
                <th className="text-right px-2 py-2">Tenor</th>
                {/* Angka tanpa "Rp" di sel (satuan di judul kolom): 13 kolom C.7
                    harus muat di layar laptop tanpa menggulung ke samping. */}
                <th className="text-right px-2 py-2">SRP (Rp)</th>
                <th className="text-right px-2 py-2">DP (Rp)</th>
                <th className="text-right px-2 py-2">Nominal (Rp)</th>
                <th className="text-left px-2 py-2">Leasing/CC</th>
                <th className="text-left px-2 py-2">Nama Konsumen</th>
                <th className="text-left px-2 py-2 min-w-[200px]">Status Claim</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {memuat && !data ? (
                <tr><td colSpan={13} className="px-2 py-8 text-center text-slate-400">Menghitung pencocokan...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={13} className="px-2 py-8 text-center text-slate-400" data-testid="mdr-kosong">
                  Tidak ada data claim {filterAktif ? "untuk filter ini" : "di tab ini"}.
                </td></tr>
              ) : items.map((b) => (
                <tr key={b.data_id} className="align-top hover:bg-slate-50/60" data-testid={`mdr-row-${b.data_id}`}>
                  <td className="px-2 py-2">
                    {bisaDicentang(b) && (
                      <input type="checkbox" checked={pilih.has(b.data_id)} onChange={() => togglePilih(b.data_id)}
                             data-testid={`mdr-centang-${b.data_id}`} />
                    )}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">{formatTanggal(b.tanggal_aktivasi)}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{formatTanggal(b.tanggal_pembelian)}
                    {b.no_nota && <p className="text-[10px] text-slate-400 font-mono">{b.no_nota}</p>}</td>
                  <td className="px-2 py-2 font-mono text-[11px]">{b.imei || "-"}</td>
                  <td className="px-2 py-2 font-mono text-[11px] break-all">{b.sn || "-"}</td>
                  <td className="px-2 py-2">{b.tipe_produk || "-"}{b.cabang && <p className="text-[10px] text-slate-400">{b.cabang}</p>}</td>
                  <td className="px-2 py-2 text-right">{b.tenor ? `${b.tenor} bln` : "-"}</td>
                  <td className="px-2 py-2 text-right font-mono">{b.srp != null ? fmtNum(b.srp) : "-"}</td>
                  <td className="px-2 py-2 text-right font-mono">{b.dp != null ? fmtNum(b.dp) : "-"}</td>
                  <td className="px-2 py-2 text-right font-mono font-semibold">{fmtNum(b.nominal)}</td>
                  <td className="px-2 py-2">{b.leasing || "-"}{b.no_kontrak && <p className="text-[10px] text-slate-400 font-mono">{b.no_kontrak}</p>}</td>
                  <td className="px-2 py-2">{b.nama_konsumen || "-"}</td>
                  <td className="px-2 py-2">
                    <StatusPill status={b.status} label={b.status_label} testid={`mdr-status-${b.data_id}`} />
                    {b.batch_kode && <span className="ml-1 text-[10px] font-mono text-slate-500">{b.batch_kode}</span>}
                    {b.pengajuan_batal && <span className="ml-1 text-[10px] text-rose-600">· diajukan batal</span>}
                    {b.alasan_teks && <p className="text-[11px] text-slate-600 mt-1 leading-snug" data-testid={`mdr-alasan-${b.data_id}`}>{b.alasan_teks}</p>}
                    {b.tinjauan && (
                      <p className="text-[11px] text-emerald-700 mt-1 leading-snug" data-testid={`mdr-tinjauan-${b.data_id}`}>
                        Diterima setelah ditinjau {b.tinjauan.oleh_nama || b.tinjauan.oleh_email || ""}: {b.tinjauan.catatan}
                      </p>
                    )}
                    {/* Aksi di bawah status (bukan kolom paling kanan): tabel 13 kolom
                        melebihi layar laptop, dan tombol di ujung kanan tersembunyi
                        sampai tabel digulir ke samping. */}
                    <div className="mt-1.5">{aksiBaris(b)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="mdr-kartu">
          {items.length === 0 ? (
            <p className="p-4 text-center text-sm text-slate-400">{memuat ? "Menghitung pencocokan..." : "Tidak ada data claim."}</p>
          ) : items.map((b) => (
            <KartuBaris key={b.data_id} testid={`mdr-kartu-${b.data_id}`}>
              <KartuBarisHeader
                kiri={<>
                  <p className="font-mono text-[11px] text-slate-500">{b.imei || "-"}</p>
                  <p className="text-sm font-semibold text-slate-900">{b.tipe_produk || "-"}</p>
                </>}
                kanan={<StatusPill status={b.status} label={b.status_label} />}
              />
              <KartuBarisMeta label="Tgl Pembelian / Aktivasi" value={`${formatTanggal(b.tanggal_pembelian)} / ${formatTanggal(b.tanggal_aktivasi)}`} />
              <KartuBarisMeta label="SN" value={b.sn || "-"} />
              <KartuBarisMeta label="Leasing / Tenor" value={`${b.leasing || "-"}${b.tenor ? ` · ${b.tenor} bln` : ""}`} />
              <KartuBarisMeta label="SRP / DP" value={`${b.srp != null ? idr(b.srp) : "-"} / ${b.dp != null ? idr(b.dp) : "-"}`} />
              <KartuBarisMeta label="Nominal" value={idr(b.nominal)} tebal />
              <KartuBarisMeta label="Konsumen" value={b.nama_konsumen || "-"} />
              {b.alasan_teks && <p className="text-[11px] text-slate-600 mt-1">{b.alasan_teks}</p>}
              {b.tinjauan && <p className="text-[11px] text-emerald-700 mt-1">Diterima setelah ditinjau: {b.tinjauan.catatan}</p>}
              <div className="flex items-center justify-between gap-2 mt-2">
                {bisaDicentang(b) ? (
                  <label className="text-xs inline-flex items-center gap-1.5">
                    <input type="checkbox" checked={pilih.has(b.data_id)} onChange={() => togglePilih(b.data_id)}
                           data-testid={`mdr-centang-${b.data_id}-m`} /> Pilih
                  </label>
                ) : <span />}
                {aksiBaris(b, "-m")}
              </div>
            </KartuBaris>
          ))}
        </DaftarKartuMobile>

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 text-xs text-slate-600 flex-wrap">
          <span data-testid="mdr-jumlah">
            {fmtNum(data?.jumlah)} baris · total nominal {idr(data?.total_nominal)}
            {data?.terpotong && <span className="text-amber-700"> · data dihitung sampai 5.000 baris — persempit filter</span>}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" className={kelasTombolKecil} disabled={halaman <= 1} onClick={() => setHalaman((h) => h - 1)}
                    data-testid="mdr-halaman-sebelum"><ChevronLeft className="w-3.5 h-3.5" /></button>
            <span className="px-2">Halaman {halaman} / {totalHalaman}</span>
            <button type="button" className={kelasTombolKecil} disabled={halaman >= totalHalaman} onClick={() => setHalaman((h) => h + 1)}
                    data-testid="mdr-halaman-berikut"><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>

      {tab && <PanelBatch tab={tab} hak={hak} versi={muatUlang} onBerubah={segarkan} />}

      {dlgImpor && <DialogImporData hak={hak} onTutup={() => setDlgImpor(false)} onSelesai={segarkan}
                                    onPilihImpor={(r) => setImporF({ id: r.id, kode: r.kode })} />}
      {dlgSrp && <DialogSrp hak={hak} onTutup={() => setDlgSrp(false)} onSelesai={segarkan} />}
      {dlgPeng && <DialogPengaturan hak={hak} onTutup={() => setDlgPeng(false)}
                                    onTersimpan={() => { setVersiPeng((n) => n + 1); segarkan(); }} />}
      {dlgKandidat && <DialogKandidat baris={dlgKandidat} hak={hak} onTutup={() => setDlgKandidat(null)} onSelesai={segarkan} />}
      {dlgTinjau && (
        <DialogAlasan judul="Terima setelah ditinjau" label="Catatan hasil tinjauan" labelYa="Terima" testid="mdr-tinjau-dialog"
                      keterangan={<>
                        <p>Alasan Perlu Review: <b>{dlgTinjau.alasan_teks}</b></p>
                        <p className="text-xs text-slate-500 mt-1">Penerimaan tercatat atas nama Anda. Kalau data nota/berkasnya berubah, baris ini kembali Perlu Review.</p>
                      </>}
                      onYa={terimaTinjauan} onTutup={() => setDlgTinjau(null)} />
      )}
      {invoice && <DialogInvoice sumber={invoice} onTutup={() => setInvoice(null)} />}
      {klaimParam && <DialogKlaim klaimId={klaimParam} hak={hak} onTutup={tutupKlaim} onBerubah={segarkan} />}
      {dlgAntrean && (
        <Modal judul="Pengajuan Batal Sudah Claim" ikon={ShieldAlert} onTutup={() => setDlgAntrean(false)} lebar="max-w-3xl"
               testid="mdr-antrean" sub="Disetujui SPV (izin Hapus Claim MDR) yang bukan pengaju.">
          <div className="space-y-2">
            {pengajuan.map((p) => (
              <div key={p.id} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`mdr-antrean-${p.id}`}>
                <div className="text-xs min-w-0">
                  <p className="font-semibold text-slate-900">{p.ringkas?.nama_konsumen || "-"} · <span className="font-mono">{p.ringkas?.imei}</span></p>
                  <p className="text-slate-500">Tab {p.tab} · {p.batch_kode || "-"} · {p.ringkas?.leasing} {idr(p.ringkas?.nominal)}</p>
                  <p className="text-slate-600 mt-1">Diajukan {p.pengajuan_batal?.oleh_nama || p.pengajuan_batal?.oleh_email}: {p.pengajuan_batal?.alasan}</p>
                  {p.cn_batch?.hidup && (
                    <p className="text-amber-800 mt-1" data-testid={`mdr-antrean-cn-${p.id}`}>
                      Batch sudah punya Credit Note {p.cn_batch.cn_code} {idr(p.cn_batch.nilai)} — sesuaikan di menu Credit Note kalau unit ini termasuk nilainya.
                    </p>
                  )}
                </div>
                <button type="button" className={kelasTombolKecil} data-testid={`mdr-antrean-buka-${p.id}`}
                        onClick={() => { setDlgAntrean(false); const n = new URLSearchParams(sp); n.set("tab", p.tab); n.set("klaim", p.id); setSp(n, { replace: true }); }}>
                  {p.boleh_setujui ? "Putuskan" : "Lihat"}
                </button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
